import { api } from "../httpClient";
import { API_ENDPOINTS } from "../config";

/**
 * Save a new booking
 * @param {Object} bookingData - Booking information with required fields
 * @returns {Promise<Object>} Booking confirmation or error response
 */
export const saveBooking = async (bookingData) => {
  try {
    const response = await api.post(API_ENDPOINTS.BOOKING_SAVE, bookingData);
    return response;
  } catch (error) {
    console.error("Error saving booking:", error);
    throw error;
  }
};



/**
 * Create a new booking
 * @param {Object} bookingData - Booking information
 * @returns {Promise<Object>} Booking confirmation
 */
export const createBooking = async (bookingData) => {
  try {
    const response = await api.post(API_ENDPOINTS.BOOKING, bookingData);
    return response;
  } catch (error) {
    console.error("Error creating booking:", error);
    throw new Error("Failed to create booking");
  }
};

/**
 * Get booking confirmation details
 * @param {string} bookingId - Booking ID
 * @returns {Promise<Object>} Booking confirmation details
 */
export const getBookingConfirmation = async (bookingId) => {
  try {
    const response = await api.get(`${API_ENDPOINTS.CONFIRMATION}/${bookingId}`);
    return response;
  } catch (error) {
    console.error("Error fetching booking confirmation:", error);
    throw new Error("Failed to fetch booking confirmation");
  }
};

/**
 * Cancel a booking
 * @param {string} bookingId - Booking ID
 * @param {string} reason - Cancellation reason
 * @returns {Promise<Object>} Cancellation confirmation
 */
// export const cancelBooking = async (bookingId, reason = "") => {
//   try {
//     const response = await api.post(`${API_ENDPOINTS.BOOKING}/${bookingId}/cancel`, { reason });
//     return response;
//   } catch (error) {
//     console.error("Error cancelling booking:", error);
//     throw new Error("Failed to cancel booking");
//   }
// };

export const cancelBooking = async (payload) => {
  try {
    // payload must include: reservationNumber, requestedIp, reason, requestedBy
    const data = await api.post(API_ENDPOINTS.CANCEL_BOOKING, payload);
    return data; // <-- already parsed data object
  } catch (error) {
    console.error("Error cancelling booking:", error);
    throw error;
  }
};

/**
 * Modify a booking
 * @param {string} bookingId - Booking ID
 * @param {Object} modifications - Booking modifications
 * @returns {Promise<Object>} Modified booking details
 */
export const modifyBooking = async (bookingId, modifications) => {
  try {
    const response = await api.put(`${API_ENDPOINTS.BOOKING}/${bookingId}`, modifications);
    return response;
  } catch (error) {
    console.error("Error modifying booking:", error);
    throw new Error("Failed to modify booking");
  }
};

/**
 * Get booking history for a customer
 * @param {string} customerId - Customer ID
 * @returns {Promise<Array>} Array of booking history
 */
export const getBookingHistory = async (customerId) => {
  try {
    const response = await api.get(`${API_ENDPOINTS.BOOKING}/history/${customerId}`);
    return response.bookings || [];
  } catch (error) {
    console.error("Error fetching booking history:", error);
    return [];
  }
};

/**
 * Validate booking data before submission
 * @param {Object} bookingData - Booking data to validate
 * @returns {Object} Validation result with errors array
 */
export const validateBookingData = (bookingData) => {
  const errors = [];

  if (!bookingData.carId) {
    errors.push("Car selection is required");
  }

  if (!bookingData.customerInfo) {
    errors.push("Customer information is required");
  } else {
    const { firstName, lastName, email, phone } = bookingData.customerInfo;
    
    if (!firstName || !lastName) {
      errors.push("Full name is required");
    }
    
    if (!email || !isValidEmail(email)) {
      errors.push("Valid email address is required");
    }
    
    if (!phone) {
      errors.push("Phone number is required");
    }
  }

  if (!bookingData.searchParams) {
    errors.push("Search parameters are required");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if email is valid
 */
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Calculate booking total with taxes and fees
 * @param {Object} carData - Car data with base price
 * @param {Object} searchParams - Search parameters for duration calculation
 * @returns {Object} Total breakdown
 */
export const calculateBookingTotal = (carData, searchParams) => {
  const basePrice = parseFloat(carData.price) || 0;
  const pickupDate = new Date(searchParams.pickupDateTime);
  const dropoffDate = new Date(searchParams.dropoffDateTime);
  
  // Calculate number of days
  const days = Math.ceil(
    (dropoffDate - pickupDate) / (1000 * 60 * 60 * 24)
  );
  
  const subtotal = basePrice * days;
  const tax = subtotal * 0.1; // 10% tax
  const fees = 25; // Fixed fees
  const total = subtotal + tax + fees;

  return {
    subtotal: subtotal.toFixed(2),
    tax: tax.toFixed(2),
    fees: fees.toFixed(2),
    total: total.toFixed(2),
    days,
  };
}; 